// Mes Annonces - Gestion des annonces du particulier
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package, PlusCircle, Search, Filter, Eye, Edit, Trash2, MoreVertical,
  Clock, CheckCircle, AlertTriangle, XCircle, TrendingUp, Heart,
  MessageCircle, Share2, RefreshCw, ChevronDown, Calendar, Euro, ArrowLeft, ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useCurrentUser } from '../../hooks/useCurrentUser';

// Statuts possibles
const STATUTS = [
  { id: 'all', label: 'Toutes', color: 'gray' },
  { id: 'active', label: 'Actives', color: 'blue', icon: CheckCircle },
  { id: 'pending', label: 'En attente', color: 'amber', icon: Clock },
  { id: 'sold', label: 'Vendues', color: 'slate', icon: Euro },
  { id: 'expired', label: 'Expirées', color: 'red', icon: XCircle },
];

// Carte d'annonce
function AnnonceCard({ annonce, onEdit, onDelete, onRenew, onMarkSold }) {
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();

  const statusConfig = {
    active: { label: 'Active', bgColor: 'bg-[#1E3A5F]/10', textColor: 'text-[#1E3A5F]', icon: CheckCircle },
    pending: { label: 'En attente', bgColor: 'bg-amber-50', textColor: 'text-amber-700', icon: Clock },
    sold: { label: 'Vendue', bgColor: 'bg-slate-100', textColor: 'text-slate-700', icon: Euro },
    expired: { label: 'Expirée', bgColor: 'bg-red-50', textColor: 'text-red-700', icon: XCircle },
  };

  const status = statusConfig[annonce.status] || statusConfig.active;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
    >
      <div className="flex flex-col sm:flex-row">
        {/* Image */}
        <div className="sm:w-48 h-40 sm:h-auto bg-gray-100 relative flex-shrink-0">
          {annonce.image ? (
            <img src={annonce.image} alt={annonce.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package size={40} className="text-gray-300" />
            </div>
          )}
          {/* Badge statut */}
          <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${status.bgColor} ${status.textColor}`}>
            <StatusIcon size={12} />
            {status.label}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 truncate mb-1">{annonce.title}</h3>
              <p className="text-xl font-bold text-orange-600 mb-2">{annonce.price} €</p>
              
              {/* Stats */}
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                <span className="flex items-center gap-1">
                  <Eye size={14} /> {annonce.views} vues
                </span>
                <span className="flex items-center gap-1">
                  <Heart size={14} /> {annonce.favorites} favoris
                </span>
                <span className="flex items-center gap-1">
                  <MessageCircle size={14} /> {annonce.messages} messages
                </span>
              </div>

              {/* Date */}
              <p className="text-xs text-gray-400 flex items-center gap-1">
                <Calendar size={12} />
                Publiée le {new Date(annonce.createdAt).toLocaleDateString('fr-FR')}
                {annonce.expiresAt && (
                  <span className="ml-2 text-orange-600">
                    • Expire le {new Date(annonce.expiresAt).toLocaleDateString('fr-FR')}
                  </span>
                )}
              </p>
            </div>

            {/* Actions Menu */}
            <div className="relative">
              <button 
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <MoreVertical size={20} className="text-gray-500" />
              </button>
              
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-20">
                    <button 
                      onClick={() => { onEdit(annonce); setShowMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Edit size={16} /> Modifier
                    </button>
                    {annonce.status === 'active' && (
                      <button 
                        onClick={() => { onMarkSold(annonce); setShowMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Euro size={16} /> Marquer comme vendue
                      </button>
                    )}
                    {(annonce.status === 'expired' || annonce.status === 'sold') && (
                      <button 
                        onClick={() => { onRenew(annonce); setShowMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <RefreshCw size={16} /> Republier
                      </button>
                    )}
                    <button 
                      onClick={() => navigate(`/annonce/${annonce.id}`)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Eye size={16} /> Voir l'annonce
                    </button>
                    <button 
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Share2 size={16} /> Partager
                    </button>
                    <hr className="my-2" />
                    <button 
                      onClick={() => { onDelete(annonce); setShowMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 size={16} /> Supprimer
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onEdit(annonce)}
              className="flex-1 sm:flex-none"
            >
              <Edit size={14} className="mr-1" /> Modifier
            </Button>
            {annonce.status === 'active' && (
              <Button 
                size="sm"
                className="flex-1 sm:flex-none bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white"
                onClick={() => onMarkSold(annonce)}
              >
                <Euro size={14} className="mr-1" /> Vendue
              </Button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function MesAnnoncesPage({ onSelectTool }) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [annonces, setAnnonces] = useState([]);
  const [filtreStatut, setFiltreStatut] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Refs et états pour le scroll des filtres mobile
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  
  // Gérer le scroll des filtres
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Données démo
  const DEMO_ANNONCES = [];

  useEffect(() => {
    // Charger les annonces selon le mode
    setTimeout(() => {
      if (user?.isDemo) {
        // Mode démo: afficher les annonces fictives
        setAnnonces(DEMO_ANNONCES);
      } else {
        // Utilisateur réel: pas d'annonces (cockpit vierge)
        setAnnonces([]);
      }
      setLoading(false);
    }, 300);
  }, [user]);

  const filteredAnnonces = annonces.filter(a => {
    if (filtreStatut !== 'all' && a.status !== filtreStatut) return false;
    if (searchQuery && !a.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const handleEdit = (annonce) => {
    navigate(`/cockpit-particulier/annonces/${annonce.id}/modifier`);
  };

  const handleDelete = (annonce) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) {
      setAnnonces(prev => prev.filter(a => a.id !== annonce.id));
    }
  };

  const handleMarkSold = (annonce) => {
    setAnnonces(prev => prev.map(a => 
      a.id === annonce.id ? { ...a, status: 'sold' } : a
    ));
  };

  const handleRenew = (annonce) => {
    setAnnonces(prev => prev.map(a => 
      a.id === annonce.id ? { ...a, status: 'active', expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() } : a
    ));
  };

  // Compteurs par statut
  const counts = {
    all: annonces.length,
    active: annonces.filter(a => a.status === 'active').length,
    pending: annonces.filter(a => a.status === 'pending').length,
    sold: annonces.filter(a => a.status === 'sold').length,
    expired: annonces.filter(a => a.status === 'expired').length,
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
      {/* Header - Style identique à Mes Notes */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {onSelectTool && (
            <button 
              onClick={() => onSelectTool('hub_marketplace', 'Marketplace')}
              className="p-1 -ml-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Retour au tableau de bord"
            >
              <ArrowLeft size={20} className="text-[#1e3a5f]" strokeWidth={2} />
            </button>
          )}
          <div>
            <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mes Annonces</h1>
            <p className="hidden sm:block text-sm text-gray-500">{counts.all} annonce{counts.all > 1 ? 's' : ''} au total</p>
          </div>
        </div>
        {/* Bouton Mobile */}
        <button 
          onClick={() => onSelectTool && onSelectTool('deposer', 'Déposer une annonce')}
          className="sm:hidden flex items-center gap-1 px-2.5 py-1.5 bg-[#F97316] text-white rounded-lg font-semibold text-[11px] whitespace-nowrap"
        >
          Nouvelle annonce
        </button>
        {/* Bouton Desktop */}
        <Button 
          onClick={() => onSelectTool && onSelectTool('deposer', 'Déposer une annonce')}
          className="hidden sm:flex bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm"
        >
          <PlusCircle size={16} />
          Nouvelle annonce
        </Button>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 p-3">
        {/* Recherche */}
        <div className="flex items-center gap-2 mb-3 px-2.5 py-1.5 border border-gray-200 rounded-lg">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Rechercher une annonce..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-xs text-gray-700 placeholder-gray-400"
          />
        </div>

        {/* Filtres Mobile - Style premium avec scroll horizontal */}
        <div className="sm:hidden">
          <div className="relative flex items-center">
            <div 
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {STATUTS.map((statut) => (
                <button
                  key={statut.id}
                  onClick={() => setFiltreStatut(statut.id)}
                  data-testid={`filter-${statut.id}`}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                    filtreStatut === statut.id
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {statut.icon && <statut.icon size={12} />}
                  {statut.label}
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                    filtreStatut === statut.id ? 'bg-white/20' : 'bg-gray-100'
                  }`}>
                    {counts[statut.id]}
                  </span>
                </button>
              ))}
            </div>
            
            {/* Flèche orange discrète */}
            {showScrollHint && (
              <div className="flex-shrink-0 flex items-center pointer-events-none">
                <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
              </div>
            )}
          </div>
        </div>

        {/* Filtres Desktop */}
        <div className="hidden sm:flex flex-wrap gap-2">
          {STATUTS.map((statut) => (
            <button
              key={statut.id}
              onClick={() => setFiltreStatut(statut.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                filtreStatut === statut.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {statut.icon && <statut.icon size={14} />}
              {statut.label}
              <span className={`px-1.5 py-0.5 rounded text-xs ${
                filtreStatut === statut.id ? 'bg-white/20' : 'bg-gray-200'
              }`}>
                {counts[statut.id]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Liste des annonces */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
        </div>
      ) : filteredAnnonces.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Package size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune annonce trouvée</h3>
          <p className="text-gray-500 mb-4">
            {searchQuery 
              ? 'Aucune annonce ne correspond à ta recherche' 
              : 'Tu n\'as pas encore d\'annonce dans cette catégorie'}
          </p>
          <Button 
            onClick={() => onSelectTool && onSelectTool('deposer', 'Déposer une annonce')}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <PlusCircle size={18} className="mr-2" />
            Créer une annonce
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAnnonces.map((annonce) => (
            <AnnonceCard
              key={annonce.id}
              annonce={annonce}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onMarkSold={handleMarkSold}
              onRenew={handleRenew}
            />
          ))}
        </div>
      )}
    </div>
  );
}
